// src/services/api/GamesApi.tsx
import { BaseAPI } from "./BaseApi";
import type { GameDetailResponse } from "../../types/game";
import type { NewsResponse } from "../../types/news";

export interface GameListItem {
  gameId: string;
  name: string;
  coverUrl: string | null;
  genres: string[];
  platforms: string[];
}

export interface GameListResponse {
  games: GameListItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

/**
 * GamesAPI Client
 *
 * Provides methods to interact with game endpoints.
 * All methods include authentication credentials.
 *
 */
export class GamesAPI extends BaseAPI {
  constructor() {
    super(""); // uses same base URL as other API classes
  }

  /**
   * List/search games with cursor-based pagination
   * GET /api/games?q=&limit=&cursor=
   */
  async listGames(params?: { q?: string; limit?: number; cursor?: string }): Promise<GameListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.q) searchParams.set("q", params.q);
    if (params?.limit) searchParams.set("limit", String(params.limit));
    if (params?.cursor) searchParams.set("cursor", params.cursor);

    const qs = searchParams.toString();
    return this.request<GameListResponse>(`/api/games${qs ? `?${qs}` : ""}`, {
      method: "GET",
      auth: true,
    });
  }

  /**
   * Fetch full game details by gameId
   * GET /api/games/:gameId
   *
   * Constructs the URL using the exact gameId string without encoding or transformation.
   *
   *
   * @param gameId - The game's UUID
   * @returns Promise resolving to game detail response
   * @throws ApiError on HTTP errors (400, 401, 404, 500)
   */
  async getGameById(gameId: string): Promise<GameDetailResponse> {
    return this.request<GameDetailResponse>(`/api/games/${gameId}`, {
      method: "GET",
      auth: true,
    });
  }

  /**
   * Fetch Steam news articles for a game
   * GET /api/games/:gameId/news
   *
   * @param gameId - The game's UUID
   * @returns Promise resolving to news response with array of news items
   * @throws ApiError on HTTP errors (400, 401, 404, 502)
   */
  async getNewsForGame(gameId: string): Promise<NewsResponse> {
    return this.request<NewsResponse>(`/api/games/${gameId}/news`, {
      method: "GET",
      auth: true,
    });
  }
}
